/**
 * The single implementation of value normalisation for intake answers (agent-design §5,
 * validator `typeAndNormalise`). Used by the API validators and the web panel alike.
 * Every parser returns null when it is not sure: a wrong number is worse than none.
 */

// ── Money ────────────────────────────────────────────────────────────────────

export interface MoneyAmount {
  /** Integer minor units (cents), CODING_STANDARDS "Money". */
  amountMinor: number;
  /** ISO 4217. */
  currency: string;
  /** For ranges ("20–30k"): the lower bound; `amountMinor` holds the upper bound. */
  minAmountMinor?: number;
}

const CURRENCY_WORDS: [RegExp, string][] = [
  [/€|\beur(?:o|os)?\b/i, "EUR"],
  [/\$|\busd\b|\bdollar[si]?\b/i, "USD"],
  [/£|\bgbp\b|\bpounds?\b|\bsterlin[ag]\w*\b/i, "GBP"],
  [/\bchf\b|\bfranchi\b|\bfrancs?\b/i, "CHF"],
];

const MULTIPLIERS: [RegExp, number][] = [
  [/^(?:k|mila|thousand|migliaia)$/i, 1_000],
  [/^(?:m|mln|mio|milion[ei]|million|millions)$/i, 1_000_000],
];

// A number (with . , or space grouping), optionally followed by a magnitude word.
const AMOUNT =
  /(\d{1,3}(?:[.,\s']\d{3})+(?:[.,]\d{1,2})?|\d+(?:[.,]\d+)?)\s*(k|mila|thousand|migliaia|m|mln|mio|milion[ei]|millions?)?\b/gi;

// Two figures form a range when joined by a dash, "to", "a" or "fino a" ("20-30k", "€100k–€150k",
// "from €100k to €150k", "da 50.000 a 80.000"), or by "and" / "e" only right after "between" / "tra" /
// "fra": a bare "€10k e €5k" is two separate amounts, not a range.
const MAGNITUDE = String.raw`(?:k|mila|thousand|migliaia|m|mln|mio|milion[ei]|millions?)`;
const ARTICLE = String.raw`(?:(?:i|gli)\s+)?`;
const RANGE_JOINED = new RegExp(
  String.raw`\d\s*${MAGNITUDE}?\s*(?:-|–|—|\bto\b|\ba\b|\bfino a\b)\s*${ARTICLE}[€$£]?\s*\d`,
  "i",
);
const RANGE_BETWEEN = new RegExp(
  String.raw`\b(?:between|tra|fra)\s+${ARTICLE}[€$£]?\s*\d[\d.,\s']*${MAGNITUDE}?\s*(?:€|\beur(?:o|os)?\b)?\s*(?:and|e)\s+${ARTICLE}[€$£]?\s*\d`,
  "i",
);

/**
 * Reads a number written the Italian ("25.000,50") or English ("25,000.50") way.
 * A single separator followed by exactly three digits is a thousands separator
 * ("25.000" = 25000), otherwise a decimal point ("1,5" = 1.5).
 */
function readNumber(raw: string): number | null {
  const s = raw.replace(/[\s']/g, "");
  const lastDot = s.lastIndexOf(".");
  const lastComma = s.lastIndexOf(",");
  let normalised: string;
  if (lastDot >= 0 && lastComma >= 0) {
    const decimalSep = lastDot > lastComma ? "." : ",";
    const groupSep = decimalSep === "." ? "," : ".";
    normalised = s.split(groupSep).join("").replace(decimalSep, ".");
  } else if (lastDot >= 0 || lastComma >= 0) {
    const sep = lastDot >= 0 ? "." : ",";
    const parts = s.split(sep);
    const groupsOfThree = parts.length > 1 && parts.slice(1).every((p) => p.length === 3);
    normalised = groupsOfThree ? parts.join("") : parts.join(".");
    if (!groupsOfThree && parts.length > 2) return null;
  } else {
    normalised = s;
  }
  const value = Number(normalised);
  return Number.isFinite(value) ? value : null;
}

function multiplierOf(word: string | undefined): number {
  if (!word) return 1;
  return MULTIPLIERS.find(([re]) => re.test(word))?.[1] ?? 1;
}

/**
 * Extracts a money amount from free text ("€25k", "25.000 euro", "1,5 milioni",
 * "budget 20-30k EUR"). Returns null when there is no figure, when it is ambiguous
 * (several unrelated figures), or when no currency is given and `defaultCurrency` is not set.
 */
export function parseMoney(text: string, options: { defaultCurrency?: string } = {}): MoneyAmount | null {
  const currency = CURRENCY_WORDS.find(([re]) => re.test(text))?.[1] ?? options.defaultCurrency;
  if (!currency) return null;
  const amounts: number[] = [];
  for (const match of text.matchAll(AMOUNT)) {
    const raw = match[1];
    if (!raw) continue;
    const value = readNumber(raw);
    if (value === null) return null;
    amounts.push(value * multiplierOf(match[2]));
  }
  if (amounts.length === 0) return null;

  const isRange = amounts.length === 2 && (RANGE_JOINED.test(text) || RANGE_BETWEEN.test(text));
  if (amounts.length > 1 && !isRange) return null;
  const toMinor = (v: number) => Math.round(v * 100);
  if (isRange) {
    let [low = 0, high = 0] = amounts;
    // "20-30k": the magnitude written once applies to both ends.
    if (low < high / 100) low *= high >= 1_000_000 ? 1_000_000 : 1_000;
    return { amountMinor: toMinor(Math.max(low, high)), minAmountMinor: toMinor(Math.min(low, high)), currency };
  }
  const [amount = 0] = amounts;
  return { amountMinor: toMinor(amount), currency };
}

/**
 * True if the text states a money figure (validator budgetGuard: a budget must be a figure,
 * never a negotiation-status sentence). Without a currency, a bare year ("approved for 2026")
 * does not count as a figure.
 */
export function containsMoneyFigure(text: string): boolean {
  const hasCurrency = CURRENCY_WORDS.some(([re]) => re.test(text));
  const candidate = hasCurrency ? text : text.replace(/\b(?:19|20)\d{2}\b/g, "");
  return parseMoney(candidate, { defaultCurrency: "EUR" }) !== null;
}

// ── Duration ─────────────────────────────────────────────────────────────────

export type DurationUnit = "day" | "week" | "month" | "year";

export interface Duration {
  amount: number;
  unit: DurationUnit;
  /** For ranges ("6-12 months"): the lower bound; `amount` holds the upper bound. */
  minAmount?: number;
}

const UNIT_WORDS: [RegExp, DurationUnit][] = [
  [/^(?:days?|giorn[oi]|gg|d)$/i, "day"],
  [/^(?:weeks?|settiman[ae]|sett|wk|w)$/i, "week"],
  [/^(?:months?|mes[ei]|mos?)$/i, "month"],
  [/^(?:years?|ann[oi]|yrs?|y)$/i, "year"],
];

const WORD_NUMBERS: Record<string, number> = {
  a: 1,
  an: 1,
  one: 1,
  un: 1,
  uno: 1,
  una: 1,
  two: 2,
  due: 2,
  three: 3,
  tre: 3,
  four: 4,
  quattro: 4,
  five: 5,
  cinque: 5,
  six: 6,
  sei: 6,
  seven: 7,
  sette: 7,
  eight: 8,
  otto: 8,
  nine: 9,
  nove: 9,
  ten: 10,
  dieci: 10,
  twelve: 12,
  dodici: 12,
  eighteen: 18,
  diciotto: 18,
  half: 0.5,
  mezzo: 0.5,
};

const NUMBER_TOKEN = /^\d+(?:[.,]\d+)?$/;
const RANGE_WORDS = new Set(["-", "–", "to", "a"]);

const toNumber = (token: string): number | undefined =>
  NUMBER_TOKEN.test(token) ? Number(token.replace(",", ".")) : WORD_NUMBERS[token];

/** Reads "6 months", "6-12 mesi", "un anno", "about two years". Null if absent or ambiguous. */
export function parseDuration(text: string): Duration | null {
  // Tokens: numbers, words and range dashes ("6-12" → "6", "-", "12").
  const tokens = text.toLowerCase().match(/\d+(?:[.,]\d+)?|[a-zà-ù]+|[-–]/g) ?? [];
  const found: Duration[] = [];
  tokens.forEach((token, i) => {
    const unit = UNIT_WORDS.find(([re]) => re.test(token))?.[1];
    if (!unit || i === 0) return;
    const amount = toNumber(tokens[i - 1] ?? "");
    if (amount === undefined || !Number.isFinite(amount) || amount <= 0) return;
    const rangeStart = RANGE_WORDS.has(tokens[i - 2] ?? "") ? toNumber(tokens[i - 3] ?? "") : undefined;
    found.push(
      rangeStart !== undefined
        ? { amount: Math.max(rangeStart, amount), minAmount: Math.min(rangeStart, amount), unit }
        : { amount, unit },
    );
  });
  return found.length === 1 ? (found[0] ?? null) : null;
}

const DAYS_PER_UNIT: Record<DurationUnit, number> = { day: 1, week: 7, month: 30.4375, year: 365.25 };

/** Approximate length in months (for tier rules such as "multi-month"). */
export function durationInMonths(duration: Duration): number {
  return (duration.amount * DAYS_PER_UNIT[duration.unit]) / DAYS_PER_UNIT.month;
}

// ── Yes / no ─────────────────────────────────────────────────────────────────

// End of word written explicitly: `\b` does not work after accented letters ("sì").
const END = String.raw`(?=$|[\s,.;:!?)\-–])`;
const YES = new RegExp(
  `^(?:yes|yeah|yep|y|true|sure|correct|certo|certamente|s[iì]|esatto|confermo|affermativo|assolutamente|ok)${END}`,
  "i",
);
const NO = new RegExp(`^(?:no|nope|n|false|not|non|nessun[oa]?|mai|negativo)${END}`, "i");

/**
 * Reads a yes/no answer in IT or EN from the start of the text ("Sì, due società" → true).
 * Returns null for anything else — including "maybe", "dipende", "not sure".
 */
export function parseYesNo(text: string): boolean | null {
  const t = text.trim().replace(/^[«"'“(]+/, "");
  if (/^(?:not sure|non so|non lo so|dipende|maybe|forse|depends)\b/i.test(t)) return null;
  if (YES.test(t)) return true;
  if (NO.test(t)) return false;
  return null;
}
